import React from "react";
import { reportRenderError } from "@/lib/api";

// Prevents a single component crash from blanking the entire app. This is
// especially important for photographers who might have hundreds of photos
// in a gallery — one bad piece of EXIF or a stale layer definition should
// not lock them out of their whole studio.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // Log to console so it still shows up in DevTools + PostHog session replay,
    // but do NOT unmount the whole app.
    // eslint-disable-next-line no-console
    console.error("[Photo Bridge] Caught render error", error, info?.componentStack);
    // Also fire a Health Beacon + PostHog `pb_render_error` event so the team
    // hears about crashes before the next customer email lands.
    try { reportRenderError(error, info); } catch { /* never throw from a catch */ }
  }
  reset = () => this.setState({ error: null });
  reload = () => window.location.reload();
  render() {
    if (this.state.error) {
      return (
        <div data-testid="error-boundary" className="min-h-screen flex items-center justify-center p-6 bg-stone-950 text-stone-100">
          <div className="max-w-md w-full text-center">
            <p className="text-xs uppercase tracking-widest text-amber-400 mb-2">Something went wrong</p>
            <h1 className="font-display text-3xl mb-3">This page hit a snag</h1>
            <p className="text-stone-400 text-sm mb-6">Your work is safe — we've logged the problem. You can try again, or reload the app.</p>
            <div className="flex items-center justify-center gap-3">
              <button data-testid="btn-error-retry" onClick={this.reset} className="px-4 py-2 rounded-md border border-white/10 hover:border-amber-400/60 text-sm">Try again</button>
              <button data-testid="btn-error-reload" onClick={this.reload} className="px-4 py-2 rounded-md bg-amber-500 text-stone-950 hover:bg-amber-400 text-sm font-medium">Reload app</button>
            </div>
            {process.env.NODE_ENV !== "production" && (
              <pre className="text-left text-[10px] text-stone-500 mt-6 bg-stone-900 p-3 rounded max-h-64 overflow-auto">
                {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
              </pre>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
